import { Apollo, APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { ApplicationConfig, inject } from '@angular/core';
import {
  ApolloClientOptions,
  ApolloLink,
  InMemoryCache,
  split,
  Observable
} from '@apollo/client/core';
import { createClient } from 'graphql-ws';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { setContext } from '@apollo/client/link/context';
import { environment } from '@env';
import { asyncMap, getMainDefinition } from '@apollo/client/utilities';
import { CognitoService } from '@services/cognito/cognito.service';
import { onError } from '@apollo/client/link/error';
import { ToastService } from '@services/ui/toast.service';
import { ERROR_TYPE } from '@constants/errors';
import { Router } from '@angular/router';
import { URLS } from '@constants/urls';
import { from } from 'rxjs';

const uri = environment.graphqlApiUrl; // <-- add the URL of the GraphQL server here

export function apolloOptionsFactory(): ApolloClientOptions<any> {
  const cognitoService = inject(CognitoService);
  const httpLink = inject(HttpLink);
  const toast = inject(ToastService);
  const router = inject(Router); // Iniettiamo il router

  const authLink = setContext(async (operation, prevContext) => {
    const basic = prevContext['basic'];

    if (basic) {
      return {
        headers: {
          ...prevContext['headers'],
          Accept: 'application/json; charset=utf-8',
          authorization: `Basic ${basic}`,
        },
      };
    }

    const token = await cognitoService.getToken();
    return {
      headers: {
        ...prevContext['headers'],
        Accept: 'application/json; charset=utf-8',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        LicenseId: cognitoService.loggedUser()?.licenseId ?? '',
      },
    };
  });


  // const authLink = new ApolloLink((operation, forward) => {
  //   const basic = operation.getContext()['basic'];
  //   const token = 'TODO' // prendere da cognitoService.getToken()
  //   console.log(basic);
  //   // TODO: questa operazione asincrona va fatta all'interno di ApolloLink
  //   // dove abbiamo accesso al Context
  //   // async () => {
  //   //   if (context?.['basic']) {
  //   //     return {
  //   //       headers: {
  //   //         ...headers,
  //   //         Accept: 'application/json; charset=utf-8',
  //   //         ...(context?.['basic'] ? { authorization: `Basic ${context?.['basic']}` } : {}),
  //   //       },
  //   //     };
  //   //   }
  //   //   const token = await cognitoService.getToken();
  //   //   return {
  //   //     headers: {
  //   //       ...headers,
  //   //       Accept: 'application/json; charset=utf-8',
  //   //       ...(token ? { authorization: `Bearer ${token}` } : {}),
  //   //       LicenseId: cognitoService.loggedUser()?.licenseId ?? '',
  //   //     },
  //   //   };
  //   // }
  //   if (basic) {
  //     operation.setContext({
  //       headers: {
  //         Accept: 'application/json; charset=utf-8',
  //         ...(basic ? { authorization: `Basic ${basic}` } : {}),
  //       },
  //     })
  //   } else {
  //     operation.setContext({
  //       headers: {
  //         Accept: 'application/json; charset=utf-8',
  //         ...(token ? { authorization: `Bearer ${token}` } : {}),
  //         LicenseId: cognitoService.loggedUser()?.licenseId ?? '',
  //       },
  //     })
  //   }
  //   return forward(operation);
  // });

  const http = onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, extensions }) => {
        const errorCode = extensions?.['code'];
        if (
          errorCode === 'UNAUTHENTICATED' ||
          message.includes('Unauthorized') ||
          (message.includes('http failure response') && message.includes('401'))
        ) {
          cognitoService.signOut();
          router.navigate([URLS.BASE, URLS.LOGIN]);
        } else if (message.includes(ERROR_TYPE.WARNING)) {
          const msg = message.replace(`${ERROR_TYPE.WARNING}.`, '');
          console.warn(`[GraphQL warning]: Message: ${msg}`);
          toast.notifyWarning(msg);
        } else if (message.includes('401')) {
          cognitoService.signOut();
        } else {
          console.error(`[GraphQL error]: Message: ${message}`);
          toast.notifyError(message);
        }
      });
    }

    if (networkError) {
      if (
        ('statusCode' in networkError && networkError.statusCode === 401) ||
        (networkError.message &&
          networkError.message.includes('http failure response') &&
          networkError.message.includes('401'))
      ) {
        cognitoService.signOut();
        router.navigate([URLS.BASE, URLS.LOGIN]);
        return;
      }
      cognitoService.signOut();
      router.navigate([URLS.BASE, URLS.LOGIN]);
      console.error('[Network error]:', networkError);
      // toast.notifyError(networkError.message);
    }

    // TODO: check if the following line can be removed
    return forward(operation);
  }).concat(ApolloLink.from([authLink, httpLink.create({ uri })]));

  const ws = new GraphQLWsLink(
    createClient({
      url: uri.replace('http://', 'ws://'),
      connectionParams: async () => {
        const token = await cognitoService.getToken();
        return {
          authorization: token ? `Bearer ${token}` : null,
          licenseId: cognitoService.loggedUser()?.licenseId ?? '',
        };
      },
    })
  );

  const link = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    ws,
    http
  );

  return {
    link,
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
      },
      query: {
        fetchPolicy: 'no-cache',
      },
      mutate: {
        fetchPolicy: 'no-cache',
      },
    },
  };
}

export const graphqlProvider: ApplicationConfig['providers'] = [
  Apollo,
  {
    provide: APOLLO_OPTIONS,
    useFactory: apolloOptionsFactory,
    deps: [HttpLink],
  },
];
